// Override + resolution-table types (Phase 2 Day 3 of
// plan/audits/Deployment_Manifest_Resolution_Hierarchy_Audit_2026-05-28.md).
//
// Wire shapes mirror src/ai_alm/api/routes/overrides.py exactly —
// keep these in lockstep when the backend schema changes.

export type ResolutionBasis =
  | 'architect_override'
  | 'intake'
  | 'profile_derived'
  | 'mechanical_derived'
  | 'srd_derived'
  | 'llm_default'
  | 'conditional_tbd'
  | 'tbd'

export type Confidence = 'high' | 'medium' | 'low'

export type RuleKind = 'mandatory' | 'default'

export type OverrideCategory =
  | 'regulator_approved_exception'
  | 'risk_accepted'
  | 'technical_constraint_documented'
  | 'soft'

export interface ShadowResolution {
  would_have_resolved_to: string
  would_have_basis: ResolutionBasis
  would_have_cites: string[]
  rule_kind: RuleKind | null
}

export interface ResolutionTraceStep {
  tier: string
  result: string
  detail?: string | null
  checked_profiles?: string[]
}

export interface DecisionRow {
  key: string
  decision: string
  decision_basis: ResolutionBasis | null
  confidence: Confidence | null
  rationale: string | null
  cites: string[]
  alternatives_considered: string[]
  resolution_trace: ResolutionTraceStep[] | null
  precondition: string | null
  is_overridden: boolean
  override_ref: string | null
  shadow_resolution: ShadowResolution | null
  /**
   * When the row is overridden, the rule_kind of the underlying
   * tier (`mandatory` requires evidence; `default` is rationale-
   * only). Drives the override-modal flavor selection.
   *
   * When NOT overridden, this is set from the row's basis: profile-
   * derived mandatory keys + intake-pinned keys are treated as
   * `mandatory` for the override-this-row flow; everything else
   * defaults to `default`.
   */
  underlying_rule_kind: RuleKind | null
}

export interface CatalogVersions {
  regulatory_profiles?: string | null
  shape_profiles?: string | null
  mechanical_derivations?: string | null
}

export interface DecisionResolutionsResponse {
  decisions: DecisionRow[]
  catalog_versions: CatalogVersions | null
  override_count: number
  resolver_enabled: boolean
  last_resort_tbd_keys: string[]
}

export interface OverrideEvidence {
  approver_name: string
  approval_date: string // ISO YYYY-MM-DD
  reference: string
  evidence_path: string
  notes?: string | null
}

export interface OverrideRequest {
  key: string
  to_value: string
  category: OverrideCategory
  rationale: string
  evidence?: OverrideEvidence | null
  created_by?: string | null
}

export interface OverrideResponse {
  ok: boolean
  index: number
  manifest_refreshed: boolean
  manifest_refresh_error: string | null
  decisions: DecisionResolutionsResponse
}

// --------------------------------------------------------------------------- //
// UI palette — basis → colour-code for the table chip
// --------------------------------------------------------------------------- //
export const BASIS_PALETTE: Record<
  ResolutionBasis,
  { label: string; chipClass: string; bgClass: string }
> = {
  architect_override: {
    label: 'Override',
    chipClass: 'bg-purple-950/40 border-purple-700/50 text-purple-200',
    bgClass: 'hover:bg-purple-950/20',
  },
  intake: {
    label: 'Intake',
    chipClass: 'bg-emerald-950/40 border-emerald-700/50 text-emerald-200',
    bgClass: 'hover:bg-emerald-950/10',
  },
  profile_derived: {
    label: 'Profile',
    chipClass: 'bg-emerald-950/40 border-emerald-700/50 text-emerald-200',
    bgClass: 'hover:bg-emerald-950/10',
  },
  mechanical_derived: {
    label: 'Mechanical',
    chipClass: 'bg-sky-950/40 border-sky-700/50 text-sky-200',
    bgClass: 'hover:bg-sky-950/10',
  },
  srd_derived: {
    label: 'SRD',
    chipClass: 'bg-sky-950/40 border-sky-700/50 text-sky-200',
    bgClass: 'hover:bg-sky-950/10',
  },
  llm_default: {
    label: 'LLM-default',
    chipClass: 'bg-amber-950/40 border-amber-700/50 text-amber-200',
    bgClass: 'hover:bg-amber-950/10',
  },
  conditional_tbd: {
    label: 'Conditional',
    chipClass: 'bg-zinc-800/60 border-zinc-600/50 text-zinc-300',
    bgClass: 'hover:bg-zinc-800/30',
  },
  tbd: {
    label: 'TBD',
    chipClass: 'bg-red-950/40 border-red-700/50 text-red-200',
    bgClass: 'hover:bg-red-950/20',
  },
}
